// src/components/payment/widgets/BasePaymentWidget.jsx

import React from 'react';
import { CreditCard, Shield, Loader, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const BasePaymentWidget = ({
                               amount,
                               orderId,
                               onPayment,
                               onRetry,
                               onCancel,
                               status = 'ready', // ready, processing, success, error
                               error = null,
                               testMode = false,
                               providerName = 'Платежная система'
                           }) => {

    // Состояние готовности к оплате
    if (status === 'ready') {
        return (
            <div className="space-y-4">
                {testMode && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-center">
                            <AlertCircle className="text-yellow-500 mr-2" size={18} />
                            <div>
                                <p className="text-sm font-medium text-yellow-800">Тестовый режим</p>
                                <p className="text-xs text-yellow-700 mt-0.5">
                                    Реальные платежи не выполняются
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 text-center">
                        Оплата через {providerName}
                    </p>
                </div>

                <button
                    onClick={onPayment}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center shadow-md"
                >
                    <CreditCard className="mr-2" size={20} />
                    Оплатить {amount} ₽
                </button>

                <div className="flex items-center justify-center space-x-4 text-xs text-gray-500 pt-2">
                    <span>Visa</span>
                    <span>Mastercard</span>
                    <span>МИР</span>
                </div>

                <div className="flex items-center justify-center text-xs text-gray-500">
                    <Shield className="mr-1 text-green-500" size={14} />
                    <span>Безопасная оплата</span>
                </div>
            </div>
        );
    }

    // Состояние обработки
    if (status === 'processing') {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <div className="relative">
                    <Loader className="animate-spin text-blue-500 mb-4" size={48} />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Обработка платежа
                </h3>
                <p className="text-gray-600 text-center">
                    Пожалуйста, не закрывайте окно
                </p>
                <p className="text-sm text-gray-500 mt-4">
                    Это может занять несколько секунд
                </p>
            </div>
        );
    }

    // Состояние успеха
    if (status === 'success') {
        return (
            <div className="flex flex-col items-center justify-center py-8 animate-in fade-in">
                <div className="relative mb-4">
                    <CheckCircle className="text-green-500" size={64} />
                    <div className="absolute -inset-2 bg-green-100 rounded-full animate-ping opacity-20"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Оплата прошла успешно!
                </h3>
                <p className="text-gray-600 text-center mb-4">
                    Заказ #{orderId} оплачен
                </p>
                {testMode && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
                        <p className="text-xs text-yellow-800">Тестовый платеж</p>
                    </div>
                )}
            </div>
        );
    }

    // Состояние ошибки
    if (status === 'error') {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <XCircle className="text-red-500 mb-4" size={64} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Ошибка оплаты
                </h3>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 w-full">
                    <p className="text-sm text-red-800 text-center">
                        {error || 'Не удалось выполнить платеж'}
                    </p>
                </div>

                <div className="flex gap-3 w-full">
                    <button
                        onClick={onRetry}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                        Попробовать снова
                    </button>
                    <button
                        onClick={onCancel}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                        Отмена
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

export default BasePaymentWidget;